import { useQuery } from "@tanstack/react-query";
import { Globe, Play } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { SearchActivityOverlay } from "@/components/search-activity-overlay";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { shouldShowSearchActivityOverlay, type SearchExecutionActivity } from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import type { BrandDashboardPeriod } from "@/state/store/userPreferences";
import type { EnrichedCitationTopItem, TopCitedPagesSource, YoutubeLiveStatsResponse } from "@/types/data";
import { formatCitationCountLabel } from "../top-cited-domains-chart";
type SourceType = TopCitedPagesSource["sourceType"];
type TabSourceType = Extract<SourceType, "reddit" | "wikipedia" | "linkedin">;
type YoutubeLiveStatItem = YoutubeLiveStatsResponse["items"][number];

interface TopCitedPagesCardsProps {
  brandId: string;
  period: BrandDashboardPeriod;
  activity?: SearchExecutionActivity | null;
}

const TOP_CITED_PAGES_LIMIT = 10;
const VISIBLE_ITEMS_PER_SOURCE = 5;
const TAB_SOURCE_TYPES: TabSourceType[] = ["reddit", "wikipedia", "linkedin"];
const REDDIT_PLACEHOLDER_TITLES = new Set(["reddit.com", "old.reddit.com", "m.reddit.com", "np.reddit.com", "redd.it"]);

const SOURCE_ICONS: Partial<Record<SourceType, string>> = {
  youtube: "/icons/youtube.png",
  reddit: "/icons/reddit.png",
  wikipedia: "/icons/social.png",
  linkedin: "/icons/linkedin.png",
};

const SOURCE_LABELS: Record<SourceType, string> = {
  web: "Top Web Pages",
  youtube: "Top YouTube Videos",
  reddit: "Reddit",
  wikipedia: "Wikipedia",
  linkedin: "LinkedIn",
};
const CITATION_ROW_CLASS_NAME =
  "group grid min-h-[70px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b px-4 py-1.5 transition-colors hover:bg-brand-ink/[0.025] last:border-b-0";

const formatShare = (share: number): string => {
  const rounded = Number.isInteger(share) ? share.toFixed(0) : share.toFixed(1);
  return `${rounded}%`;
};

const formatYoutubeViewCountLabel = (viewCount: number): string =>
  `${new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: viewCount >= 1000 ? 1 : 0,
  }).format(viewCount)} views`;

function buildYoutubeMetadata(
  channelName: string | null | undefined,
  viewCount: number | null | undefined,
): {
  channelName: string | null;
  viewCountLabel: string | null;
} {
  return {
    channelName: channelName?.trim() || null,
    viewCountLabel: typeof viewCount === "number" ? formatYoutubeViewCountLabel(viewCount) : null,
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function extractSubreddit(url: string): string | null {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments[0]?.toLowerCase() === "r" && segments[1]) {
      return `r/${segments[1]}`;
    }
  } catch {
    // noop
  }
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatCompactCitationUrl(value: string): string {
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.replace(/^www\./, "");
    const pathname = parsed.pathname === "/" ? "" : parsed.pathname;
    return `${host}${pathname}${parsed.search}`;
  } catch {
    return value.replace(/^https?:\/\//, "");
  }
}

function humanizeUrlSlug(value: string): string {
  return decodeURIComponent(value)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function resolveRedditTitle(item: EnrichedCitationTopItem): string {
  const title = item.title?.trim();
  if (title && !REDDIT_PLACEHOLDER_TITLES.has(title.toLowerCase())) {
    return title;
  }

  try {
    const parsed = new URL(item.url);
    const segments = parsed.pathname.split("/").filter(Boolean);

    if (segments[0]?.toLowerCase() === "r" && segments[2]?.toLowerCase() === "comments" && segments[4]) {
      const slugTitle = humanizeUrlSlug(segments[4]);
      if (slugTitle) {
        return slugTitle;
      }
    }

    if (segments[0]?.toLowerCase() === "comments" && segments[2]) {
      const slugTitle = humanizeUrlSlug(segments[2]);
      if (slugTitle) {
        return slugTitle;
      }
    }
  } catch {
    // noop
  }

  return formatCompactCitationUrl(item.url);
}

function getYoutubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    if (host === "youtu.be") {
      return parsed.pathname.split("/").filter(Boolean)[0]?.trim() ?? null;
    }

    const pathSegments = parsed.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0]?.toLowerCase();
    if (firstSegment === "watch") {
      return parsed.searchParams.get("v")?.trim() ?? null;
    }

    if ((firstSegment === "shorts" || firstSegment === "embed" || firstSegment === "live") && pathSegments[1]) {
      return pathSegments[1].trim();
    }
  } catch {
    // noop
  }

  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function getYoutubeThumbnailUrl(item: Pick<EnrichedCitationTopItem, "url">): string | null {
  const videoId = getYoutubeVideoId(item.url);
  if (!videoId) {
    return null;
  }

  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

function SourceIcon({ sourceType, className }: { sourceType: SourceType; className?: string }) {
  const iconSrc = SOURCE_ICONS[sourceType];

  if (!iconSrc) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-full bg-muted/35 text-muted-foreground",
          className,
        )}
      >
        <Globe className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    );
  }

  return (
    <img
      src={iconSrc}
      alt=""
      aria-hidden="true"
      className={cn("object-contain shrink-0", className)}
    />
  );
}

function CitationMetricRail({ item }: { item: EnrichedCitationTopItem }) {
  return (
    <div className="flex shrink-0 flex-col items-end gap-1 text-right">
      <div className="text-[12px] font-semibold tabular-nums text-foreground/80">{formatShare(item.share)}</div>
      <span className="text-[11px] text-muted-foreground/70">
        {formatCitationCountLabel(item.count)}
      </span>
    </div>
  );
}

function FaviconAvatar({ item }: { item: EnrichedCitationTopItem }) {
  const [hasError, setHasError] = useState(false);

  if (!item.favicon || hasError) {
    return (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-muted/30 text-muted-foreground">
        <Globe className="h-4 w-4" aria-hidden="true" />
      </span>
    );
  }

  return (
    <img
      src={item.favicon}
      alt=""
      className="h-10 w-10 shrink-0 rounded-xl border border-border/60 bg-background object-cover"
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
}

function CompactSourceAvatar({
  item,
  sourceType,
}: {
  item: EnrichedCitationTopItem;
  sourceType: TabSourceType;
}) {
  const [hasError, setHasError] = useState(false);
  const fallbackIcon = SOURCE_ICONS[sourceType];

  if (item.favicon && !hasError) {
    return (
      <img
        src={item.favicon}
        alt=""
        aria-hidden="true"
        className="h-5 w-5 shrink-0 rounded-md bg-background object-cover"
        loading="lazy"
        onError={() => setHasError(true)}
      />
    );
  }

  if (fallbackIcon) {
    return (
      <img
        src={fallbackIcon}
        alt=""
        aria-hidden="true"
        className="h-5 w-5 shrink-0 rounded-md bg-background object-contain p-0.5"
      />
    );
  }

  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-muted/30 text-muted-foreground">
      <Globe className="h-3 w-3" aria-hidden="true" />
    </span>
  );
}

function YoutubeThumbnail({ item }: { item: EnrichedCitationTopItem }) {
  const [hasError, setHasError] = useState(false);
  const thumbnailUrl = getYoutubeThumbnailUrl(item);

  if (!thumbnailUrl || hasError) {
    return (
      <span className="flex h-11 w-[4.5rem] shrink-0 items-center justify-center rounded-xl border border-border/60 bg-muted/25">
        <SourceIcon sourceType="youtube" className="h-5 w-5" />
      </span>
    );
  }

  return (
    <img
      src={thumbnailUrl}
      alt=""
      className="h-11 w-[4.5rem] shrink-0 rounded-xl border border-border/60 object-cover"
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
}

function YoutubeMetadata({
  channelName,
  viewCount,
}: {
  channelName: string | null | undefined;
  viewCount: number | null | undefined;
}) {
  const metadata = buildYoutubeMetadata(channelName, viewCount);

  if (!metadata.viewCountLabel && !metadata.channelName) {
    return null;
  }

  return (
    <p className="mt-0.5 overflow-hidden text-[11px] leading-4 text-muted-foreground/70">
      <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
        {metadata.viewCountLabel ? (
          <span className="inline-flex shrink-0 items-center gap-1 tabular-nums">
            <Play
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0"
            />
            <span>{metadata.viewCountLabel}</span>
          </span>
        ) : null}
        {metadata.viewCountLabel && metadata.channelName ? (
          <span aria-hidden="true" className="shrink-0">
            •
          </span>
        ) : null}
        {metadata.channelName ? <span className="min-w-0 truncate">{metadata.channelName}</span> : null}
      </span>
    </p>
  );
}

function CardShell({
  title,
  iconSourceType,
  header,
  activity,
  children,
}: {
  title?: string;
  iconSourceType?: SourceType;
  header?: ReactNode;
  activity?: SearchExecutionActivity | null;
  children: ReactNode;
}) {
  const showLoadingOverlay = shouldShowSearchActivityOverlay(activity);
  const defaultHeader =
    title && iconSourceType ? (
      <div className="flex min-w-0 items-center gap-2.5">
        <SourceIcon sourceType={iconSourceType} className="h-4 w-4" />
        <span className="truncate text-[15px] font-semibold">{title}</span>
      </div>
    ) : null;

  return (
    <Card className="flex h-full flex-col gap-0 overflow-hidden rounded-xl p-0 shadow-sm">
      <div className="flex h-12 items-center border-b px-5">
        {header ?? defaultHeader}
      </div>

      <div className="relative flex flex-1 flex-col">
        {showLoadingOverlay && <SearchActivityOverlay activity={activity} showElapsedTime={false} />}
        {children}
      </div>
    </Card>
  );
}

function EmptyBody() {
  return (
    <div className="flex flex-1 items-center justify-center px-5 py-8 text-[11px] text-muted-foreground/45">
      No data for this period
    </div>
  );
}

function WebPageRow({ item }: { item: EnrichedCitationTopItem }) {
  const title = item.title?.trim() || formatCompactCitationUrl(item.url);
  const compactUrl = formatCompactCitationUrl(item.display || item.url);

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={CITATION_ROW_CLASS_NAME}
    >
      <div className="flex min-w-0 items-start gap-3">
        <FaviconAvatar item={item} />
        <div className="min-w-0">
          <p className="line-clamp-1 text-[13px] font-medium leading-5 text-foreground/90">{title}</p>
          <p className="mt-0.5 line-clamp-1 text-[11px] leading-4 text-muted-foreground/70">{compactUrl}</p>
        </div>
      </div>
      <CitationMetricRail item={item} />
    </a>
  );
}

function YoutubeRow({
  item,
  youtubeLiveStatsByVideoId,
}: {
  item: EnrichedCitationTopItem;
  youtubeLiveStatsByVideoId: Map<string, YoutubeLiveStatItem>;
}) {
  const title = item.title?.trim() || "Untitled video";
  const videoId = getYoutubeVideoId(item.url);
  const liveViewCount = videoId ? youtubeLiveStatsByVideoId.get(videoId)?.viewCount : null;

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={CITATION_ROW_CLASS_NAME}
    >
      <div className="flex min-w-0 items-start gap-3">
        <YoutubeThumbnail item={item} />
        <div className="min-w-0 self-center">
          <p className="line-clamp-1 text-[13px] font-medium leading-5 text-foreground/90">{title}</p>
          <YoutubeMetadata
            channelName={item.youtubeChannelName}
            viewCount={liveViewCount}
          />
        </div>
      </div>
      <CitationMetricRail item={item} />
    </a>
  );
}

function SourceRow({ item, sourceType }: { item: EnrichedCitationTopItem; sourceType: TabSourceType }) {
  const subreddit = sourceType === "reddit" ? extractSubreddit(item.url) : null;
  const title =
    sourceType === "reddit"
      ? resolveRedditTitle(item)
      : sourceType === "wikipedia"
      ? item.title?.trim() || item.display
      : item.title?.trim() || formatCompactCitationUrl(item.url);
  const compactUrl = formatCompactCitationUrl(item.display || item.url);
  const secondaryLabel =
    sourceType === "reddit" && subreddit
      ? subreddit
      : subreddit
        ? `${subreddit} • ${compactUrl}`
        : compactUrl;
  const titleClampClassName = sourceType === "reddit" ? "line-clamp-1" : "line-clamp-2";

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={CITATION_ROW_CLASS_NAME}
    >
      <div className="flex min-w-0 items-center gap-3">
        <CompactSourceAvatar item={item} sourceType={sourceType} />
        <div className="min-w-0">
          <p className={cn(titleClampClassName, "text-[13px] font-medium leading-5 text-foreground/90")}>{title}</p>
          <p className="mt-0.5 line-clamp-1 text-[11px] leading-4 text-muted-foreground/70">{secondaryLabel}</p>
        </div>
      </div>
      <CitationMetricRail item={item} />
    </a>
  );
}

function CitationRows({
  items,
  sourceType,
  youtubeLiveStatsByVideoId,
}: {
  items: EnrichedCitationTopItem[];
  sourceType: SourceType;
  youtubeLiveStatsByVideoId?: Map<string, YoutubeLiveStatItem>;
}) {
  if (items.length === 0) {
    return <EmptyBody />;
  }

  if (sourceType === "web") {
    return (
      <div className="flex flex-1 flex-col">
        {items.map((item) => (
          <WebPageRow key={item.key} item={item} />
        ))}
      </div>
    );
  }

  if (sourceType === "youtube") {
    const resolvedYoutubeLiveStatsByVideoId = youtubeLiveStatsByVideoId ?? new Map<string, YoutubeLiveStatItem>();
    return (
      <div className="flex flex-1 flex-col">
        {items.map((item) => (
          <YoutubeRow
            key={item.key}
            item={item}
            youtubeLiveStatsByVideoId={resolvedYoutubeLiveStatsByVideoId}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      {items.map((item) => (
        <SourceRow key={item.key} item={item} sourceType={sourceType} />
      ))}
    </div>
  );
}

function CitationCardSkeleton({ withMedia }: { withMedia: boolean }) {
  return (
    <Card className="flex h-full flex-col gap-0 overflow-hidden rounded-xl p-0 shadow-sm">
      <div className="flex h-12 items-center justify-between border-b px-5">
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-4 w-4 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        {Array.from({ length: VISIBLE_ITEMS_PER_SOURCE }).map((_, index) => (
          <div
            key={index}
            className="grid min-h-[70px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b px-4 py-1.5 last:border-b-0"
          >
            <div className="flex min-w-0 items-start gap-3">
              {withMedia ? (
                <Skeleton className="h-11 w-[4.5rem] shrink-0 rounded-xl" />
              ) : (
                <Skeleton className="h-10 w-10 shrink-0 rounded-xl" />
              )}
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-3.5 w-full max-w-[14rem]" />
                <Skeleton className="h-3 w-full max-w-[10rem]" />
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="ml-auto h-3.5 w-10" />
              <Skeleton className="ml-auto h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function WebPagesCard({
  source,
  activity,
  isLoading,
}: {
  source: TopCitedPagesSource | null;
  activity?: SearchExecutionActivity | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <CitationCardSkeleton withMedia={false} />;
  }

  const items = source?.topItems.slice(0, VISIBLE_ITEMS_PER_SOURCE) ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <CardShell
      title="Top Web Pages"
      iconSourceType="web"
      activity={activity}
    >
      <CitationRows items={items} sourceType="web" />
    </CardShell>
  );
}

function YoutubeCard({
  source,
  activity,
  isLoading,
  youtubeLiveStatsByVideoId,
}: {
  source: TopCitedPagesSource | null;
  activity?: SearchExecutionActivity | null;
  isLoading: boolean;
  youtubeLiveStatsByVideoId: Map<string, YoutubeLiveStatItem>;
}) {
  if (isLoading) {
    return <CitationCardSkeleton withMedia={true} />;
  }

  const items = source?.topItems.slice(0, VISIBLE_ITEMS_PER_SOURCE) ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <CardShell
      title="Top YouTube Videos"
      iconSourceType="youtube"
      activity={activity}
    >
      <CitationRows
        items={items}
        sourceType="youtube"
        youtubeLiveStatsByVideoId={youtubeLiveStatsByVideoId}
      />
    </CardShell>
  );
}

function CommunitySourcesCard({
  sourceMap,
  activity,
  isLoading,
}: {
  sourceMap: Partial<Record<SourceType, TopCitedPagesSource>>;
  activity?: SearchExecutionActivity | null;
  isLoading: boolean;
}) {
  const [activeTab, setActiveTab] = useState<TabSourceType>("reddit");
  const availableTabSources = useMemo(
    () =>
      TAB_SOURCE_TYPES.filter((sourceType) => {
        const items = sourceMap[sourceType]?.topItems.slice(0, VISIBLE_ITEMS_PER_SOURCE) ?? [];
        return items.length > 0;
      }),
    [sourceMap],
  );

  useEffect(() => {
    if (!isLoading && availableTabSources.length > 0 && !availableTabSources.includes(activeTab)) {
      setActiveTab(availableTabSources[0]);
    }
  }, [activeTab, availableTabSources, isLoading]);

  if (isLoading) {
    return (
      <Card className="flex h-full flex-col gap-0 overflow-hidden rounded-xl p-0 shadow-sm">
        <div className="flex h-12 items-center border-b px-5">
          <div className="flex gap-2 rounded-lg border border-brand-ink/12 bg-brand-ink/[0.025] p-[3px]">
            <Skeleton className="h-6 w-16 rounded-md" />
            <Skeleton className="h-6 w-20 rounded-md" />
            <Skeleton className="h-6 w-16 rounded-md" />
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          {Array.from({ length: VISIBLE_ITEMS_PER_SOURCE }).map((_, index) => (
            <div
              key={index}
              className="grid min-h-[70px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b px-4 py-1.5 last:border-b-0"
            >
              <div className="min-w-0 space-y-2">
                <Skeleton className="h-3.5 w-full max-w-[14rem]" />
                <Skeleton className="h-3 w-full max-w-[10rem]" />
              </div>
              <div className="space-y-2">
                <Skeleton className="ml-auto h-3.5 w-10" />
                <Skeleton className="ml-auto h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (availableTabSources.length === 0) {
    return null;
  }

  const resolvedActiveTab = availableTabSources.includes(activeTab) ? activeTab : availableTabSources[0];

  return (
    <Tabs
      value={resolvedActiveTab}
      onValueChange={(value) => setActiveTab(value as TabSourceType)}
      className="h-full gap-0"
    >
      <CardShell
        header={
          <TabsList
            className="!grid h-8 min-w-0 !w-full grid-flow-col gap-1 sm:!inline-flex sm:!w-fit"
            style={{ gridTemplateColumns: `repeat(${availableTabSources.length}, minmax(0, 1fr))` }}
          >
            {availableTabSources.map((sourceType) => (
              <TabsTrigger
                key={sourceType}
                value={sourceType}
                className="min-w-0 !basis-auto gap-1 px-1.5 text-[10px] sm:px-2.5 sm:text-xs"
              >
                <SourceIcon sourceType={sourceType} className="h-3.5 w-3.5" />
                <span className="truncate">{SOURCE_LABELS[sourceType]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        }
        activity={activity}
      >
        {availableTabSources.map((sourceType) => {
          const source = sourceMap[sourceType] ?? null;
          const items = source?.topItems.slice(0, VISIBLE_ITEMS_PER_SOURCE) ?? [];

          return (
            <TabsContent key={sourceType} value={sourceType} className="mt-0 flex flex-1 flex-col">
              <CitationRows items={items} sourceType={sourceType} />
            </TabsContent>
          );
        })}
      </CardShell>
    </Tabs>
  );
}

export const TopCitedPagesCards = ({ brandId, period, activity = null }: TopCitedPagesCardsProps) => {
  const trpc = useTRPC();

  const { data, isLoading } = useQuery(
    trpc.charts.getTopCitedPagesData.queryOptions({
      brandId,
      limit: TOP_CITED_PAGES_LIMIT,
      ...getDisplayPeriodQuery(period),
    }),
  );

  const youtubeItems = useMemo(
    () =>
      data?.sources
        .find((source) => source.sourceType === "youtube")
        ?.topItems?.slice(0, VISIBLE_ITEMS_PER_SOURCE) ?? [],
    [data],
  );
  const youtubeVideoIds = useMemo(
    () =>
      Array.from(
        new Set(
          youtubeItems
            .map((item) => getYoutubeVideoId(item.url))
            .filter((videoId): videoId is string => Boolean(videoId)),
        ),
      ),
    [youtubeItems],
  );
  const { data: youtubeLiveStats } = useQuery(
    trpc.charts.getYoutubeVideoLiveStats.queryOptions(
      { videoIds: youtubeVideoIds },
      {
        enabled: youtubeVideoIds.length > 0,
      },
    ),
  );
  const youtubeLiveStatsByVideoId = useMemo(
    () =>
      new Map(
        (youtubeLiveStats?.items ?? []).map((item) => [item.videoId, item] as const),
      ),
    [youtubeLiveStats],
  );

  const sourceMap = useMemo(() => {
    const map: Partial<Record<SourceType, TopCitedPagesSource>> = {};
    for (const source of data?.sources ?? []) {
      map[source.sourceType] = source;
    }
    return map;
  }, [data]);

  const cards = useMemo(() => {
    if (isLoading) {
      return [
        <WebPagesCard key="web" source={sourceMap.web ?? null} activity={activity} isLoading={true} />,
        <YoutubeCard
          key="youtube"
          source={sourceMap.youtube ?? null}
          activity={activity}
          isLoading={true}
          youtubeLiveStatsByVideoId={youtubeLiveStatsByVideoId}
        />,
        <CommunitySourcesCard key="community" sourceMap={sourceMap} activity={activity} isLoading={true} />,
      ];
    }

    const populatedCards: ReactNode[] = [];
    const webItems = sourceMap.web?.topItems.slice(0, VISIBLE_ITEMS_PER_SOURCE) ?? [];
    const visibleYoutubeItems = sourceMap.youtube?.topItems.slice(0, VISIBLE_ITEMS_PER_SOURCE) ?? [];
    const hasCommunityItems = TAB_SOURCE_TYPES.some(
      (sourceType) => (sourceMap[sourceType]?.topItems.slice(0, VISIBLE_ITEMS_PER_SOURCE).length ?? 0) > 0,
    );

    if (webItems.length > 0) {
      populatedCards.push(
        <WebPagesCard key="web" source={sourceMap.web ?? null} activity={activity} isLoading={false} />,
      );
    }

    if (visibleYoutubeItems.length > 0) {
      populatedCards.push(
        <YoutubeCard
          key="youtube"
          source={sourceMap.youtube ?? null}
          activity={activity}
          isLoading={false}
          youtubeLiveStatsByVideoId={youtubeLiveStatsByVideoId}
        />,
      );
    }

    if (hasCommunityItems) {
      populatedCards.push(
        <CommunitySourcesCard key="community" sourceMap={sourceMap} activity={activity} isLoading={false} />,
      );
    }

    return populatedCards;
  }, [activity, isLoading, sourceMap, youtubeLiveStatsByVideoId]);

  if (!isLoading && cards.length === 0) {
    return null;
  }

  const gridColsClass =
    cards.length >= 3 ? "lg:grid-cols-3" : cards.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1";

  return (
    <div className={cn("grid grid-cols-1 gap-4", gridColsClass)}>
      {cards}
    </div>
  );
};

/** @deprecated Use TopCitedPagesCards instead */
export const TopCitedPagesCard = TopCitedPagesCards;
